// A sport is a rules file, not code. Adding sevens or rugby league means adding an entry here.

export interface ScoreType {
  kind: string;          // short code used in commands and the event log
  label: string;         // what the scorer and the crowd see
  pts: number;
  moment?: boolean;      // triggers a full-screen celebration on the Stage
  photo?: boolean;       // the scorer is asked who scored, so the celebration can show their photo
  follows?: string;      // offered straight after this one (a conversion follows a try)
  isTry?: boolean;       // the sport's main score: a try (rugby, also for try bonus points) or a goal (football)
  ownGoal?: boolean;     // scored by the other side for this one: listed as "Own goal" among the scorers
  small?: boolean;       // a less common score: a small button on the scorer's phone
}

export interface CardType {
  card: string;
  label: string;
  minutes: number | null; // null = no timer (off for the rest of the match, or just a caution)
  onBoard?: boolean;      // shown by the team's name on the scoreboard for the rest of the match (football's red cards)
}

export interface Period {
  id: string;
  label: string;
  startMin: number;       // clock reading when the period kicks off (rugby's 2nd half starts at 40:00)
  lengthMin: number;
}

export interface Rules {
  id: string;
  name: string;
  periods: Period[];
  scoring: ScoreType[];
  cards: CardType[];
  /** League bonus points: one for scoring this many tries, one for losing by this many points or fewer. */
  bonus?: { tries: number; losingMargin: number };
  /** League table points for a win and a draw (a loss is 0), for the season record. */
  leaguePoints: { win: number; draw: number };
  /** Football: the scorer can show the added time ("+3") when the fourth official's board goes up. */
  addedTime?: boolean;
  /** Football: the full-time summary gives each scorer's minutes (Smith 23', 67') rather than a count. */
  scorerMinutes?: boolean;
  /** Today's team: shirt numbers up to this start, higher numbers are on the bench. */
  starters: number;
  words: {
    score: string;        // the main score in a sentence: 'try', 'goal'
    scores: string;       // 'tries', 'goals'
    bench: string;        // 'Replacements', 'Substitutes'
  };
}

export const RUGBY_UNION: Rules = {
  id: 'rugby-union',
  name: 'Rugby union',
  periods: [
    { id: '1H', label: '1st half', startMin: 0, lengthMin: 40 },
    { id: '2H', label: '2nd half', startMin: 40, lengthMin: 40 },
  ],
  scoring: [
    { kind: 'T', label: 'Try', pts: 5, moment: true, photo: true, follows: 'C', isTry: true },
    { kind: 'C', label: 'Conversion', pts: 2, moment: true },
    { kind: 'P', label: 'Penalty', pts: 3, moment: true },
    { kind: 'DG', label: 'Drop goal', pts: 3, moment: true },
    { kind: 'PT', label: 'Penalty try', pts: 7, moment: true, isTry: true, small: true },
  ],
  cards: [
    { card: 'Y', label: 'Yellow card', minutes: 10 },
    { card: 'R', label: 'Red card', minutes: null },
  ],
  bonus: { tries: 4, losingMargin: 7 },     // the RFU league standard
  leaguePoints: { win: 4, draw: 2 },
  starters: 15,
  words: { score: 'try', scores: 'tries', bench: 'Replacements' },
};

/** Association football (soccer), as played on Saturdays and Sundays in English grassroots and non-league football. */
export const FOOTBALL: Rules = {
  id: 'football',
  name: 'Football',
  periods: [
    { id: '1H', label: '1st half', startMin: 0, lengthMin: 45 },
    { id: '2H', label: '2nd half', startMin: 45, lengthMin: 45 },
  ],
  scoring: [
    { kind: 'G', label: 'Goal', pts: 1, moment: true, photo: true, isTry: true },
    { kind: 'OG', label: 'Own goal', pts: 1, moment: true, isTry: true, ownGoal: true, small: true },
  ],
  cards: [
    { card: 'Y', label: 'Yellow card', minutes: null },
    // the FA's temporary dismissal for dissent, used at step 7 and below and in youth football
    { card: 'SB', label: 'Sin bin', minutes: 10 },
    { card: 'R', label: 'Red card', minutes: null, onBoard: true },
  ],
  leaguePoints: { win: 3, draw: 1 },
  addedTime: true,
  scorerMinutes: true,
  starters: 11,
  words: { score: 'goal', scores: 'goals', bench: 'Substitutes' },
};

export const RULES: Record<string, Rules> = { [RUGBY_UNION.id]: RUGBY_UNION, [FOOTBALL.id]: FOOTBALL };
export const SPORTS = [RUGBY_UNION, FOOTBALL].map(r => ({ id: r.id, name: r.name }));

export const getRules = (id: string): Rules => RULES[id] ?? RUGBY_UNION;
